import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as mediaService from '@/services/media.service';
import { MediaItem, MediaCategory, MediaStats } from '@/services/media.service';
import { ApiError } from '@/services/api';

interface MediaState {
  items: MediaItem[];
  currentItem: MediaItem | null;
  stats: MediaStats | null;
  isLoading: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  error: string | null;
  uploadProgress: number;
}

const initialState: MediaState = {
  items: [],
  currentItem: null,
  stats: null,
  isLoading: false,
  isUploading: false,
  isDeleting: false,
  isUpdating: false,
  error: null,
  uploadProgress: 0,
};

// Async thunks
export const uploadMedia = createAsyncThunk<
  MediaItem,
  { file: File; category?: MediaCategory; caption?: string; isPublic?: boolean },
  { rejectValue: string }
>('media/upload', async ({ file, category, caption, isPublic }, { rejectWithValue }) => {
  try {
    return await mediaService.uploadMedia(file, { category, caption, isPublic });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Hochladen der Datei');
  }
});

export const uploadMultipleMedia = createAsyncThunk<
  MediaItem[],
  { files: File[]; category?: MediaCategory; isPublic?: boolean },
  { rejectValue: string }
>('media/uploadMultiple', async ({ files, category, isPublic }, { rejectWithValue }) => {
  try {
    return await mediaService.uploadMultipleMedia(files, { category, isPublic });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Hochladen der Dateien');
  }
});

export const fetchMediaItems = createAsyncThunk<
  MediaItem[],
  { uploadedBy?: string; category?: MediaCategory; limit?: number; offset?: number },
  { rejectValue: string }
>('media/fetchItems', async ({ uploadedBy, category, limit, offset }, { rejectWithValue }) => {
  try {
    return await mediaService.getMediaItems(uploadedBy, category, limit, offset);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Medien');
  }
});

export const fetchMediaItemById = createAsyncThunk<
  MediaItem,
  string,
  { rejectValue: string }
>('media/fetchItemById', async (mediaId, { rejectWithValue }) => {
  try {
    return await mediaService.getMediaItemById(mediaId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden des Mediums');
  }
});

export const updateMediaItem = createAsyncThunk<
  MediaItem,
  { id: string; data: { caption?: string; isPublic?: boolean; category?: MediaCategory } },
  { rejectValue: string }
>('media/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await mediaService.updateMediaItem(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren des Mediums');
  }
});

export const deleteMediaItem = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('media/delete', async (mediaId, { rejectWithValue }) => {
  try {
    await mediaService.deleteMediaItem(mediaId);
    return mediaId;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Löschen des Mediums');
  }
});

export const fetchEventMedia = createAsyncThunk<
  MediaItem[],
  string,
  { rejectValue: string }
>('media/fetchEventMedia', async (eventId, { rejectWithValue }) => {
  try {
    return await mediaService.getEventMedia(eventId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Event-Medien');
  }
});

export const fetchMediaStats = createAsyncThunk<
  MediaStats,
  string | undefined,
  { rejectValue: string }
>('media/fetchStats', async (uploadedBy, { rejectWithValue }) => {
  try {
    return await mediaService.getMediaStats(uploadedBy);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Statistiken');
  }
});

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Upload media
    builder
      .addCase(uploadMedia.pending, (state) => {
        state.isUploading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadMedia.fulfilled, (state, action) => {
        state.isUploading = false;
        state.items.unshift(action.payload);
        state.uploadProgress = 100;
      })
      .addCase(uploadMedia.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload || 'Upload fehlgeschlagen';
        state.uploadProgress = 0;
      });

    // Upload multiple media
    builder
      .addCase(uploadMultipleMedia.pending, (state) => {
        state.isUploading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadMultipleMedia.fulfilled, (state, action) => {
        state.isUploading = false;
        state.items = [...action.payload, ...state.items];
        state.uploadProgress = 100;
      })
      .addCase(uploadMultipleMedia.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload || 'Upload fehlgeschlagen';
        state.uploadProgress = 0;
      });

    // Fetch media items
    builder
      .addCase(fetchMediaItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMediaItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchMediaItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Laden fehlgeschlagen';
      });

    // Fetch media item by ID
    builder
      .addCase(fetchMediaItemById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMediaItemById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentItem = action.payload;
      })
      .addCase(fetchMediaItemById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Laden fehlgeschlagen';
      });

    // Update media item
    builder
      .addCase(updateMediaItem.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateMediaItem.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentItem?.id === action.payload.id) {
          state.currentItem = action.payload;
        }
      })
      .addCase(updateMediaItem.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || 'Aktualisierung fehlgeschlagen';
      });

    // Delete media item
    builder
      .addCase(deleteMediaItem.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteMediaItem.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        if (state.currentItem?.id === action.payload) {
          state.currentItem = null;
        }
      })
      .addCase(deleteMediaItem.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload || 'Löschen fehlgeschlagen';
      });

    // Fetch event media
    builder
      .addCase(fetchEventMedia.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEventMedia.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchEventMedia.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Laden fehlgeschlagen';
      });

    // Fetch media stats
    builder
      .addCase(fetchMediaStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMediaStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchMediaStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Laden fehlgeschlagen';
      });
  },
});

export const { clearError, clearCurrentItem, setUploadProgress } = mediaSlice.actions;
export default mediaSlice.reducer;
